import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { apiError } from '../lib/api';
import type { CartLine, Customization, PriceEstimate, Product, Variant } from '../types';

function hasCustomization(c: Customization): boolean {
  return Boolean(c.logo_size || c.artwork_ref || c.text);
}

// Identity signature for cart de-duplication: two lines are "the same item" when
// they share a product, a variant, AND an identical customization. Blank lines
// (customization {}) collapse together so re-adding a plain item bumps its qty
// instead of stacking a second row. Personalized lines each carry a unique
// artwork_ref/text, so their signatures differ and they stay separate.
function customizationSig(c: Customization): string {
  const entries = Object.entries(c)
    .filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .sort();
  return entries.join('|');
}

function lineSig(product: Product, variant: Variant | null, c: Customization): string {
  return `${product.id}:${variant?.id ?? 0}:${customizationSig(c)}`;
}

interface CartState {
  lines: CartLine[];
  // Order-level "need it by" deadline (Y-m-d), captured in the designer and
  // carried to checkout where it's persisted onto the quote. '' = unset.
  neededBy: string;
  estimate: PriceEstimate | null;
  estimating: boolean;
  estimateError: string | null;
  // M10: product ids the estimate reported as unavailable, so the cart can flag
  // exactly which line(s) to remove instead of a blanket error.
  unavailableProductIds: number[];
  addLine: (product: Product, variant: Variant | null, customization: Customization, qty?: number) => void;
  updateQty: (key: string, qty: number) => void;
  removeLine: (key: string) => void;
  updateLine: (
    key: string,
    patch: { customization: Customization; variant?: Variant | null; qty?: number },
  ) => void;
  setNeededBy: (date: string) => void;
  clear: () => void;
  refreshEstimate: () => Promise<void>;
}

// Monotonic suffix so two lines added in the same millisecond get DISTINCT
// keys. Line keys are addressable (the "Edit design" flow targets a line by
// key), so a collision would let one edit clobber a sibling row. Date.now()
// alone collides on same product+variant within a ms; the counter breaks ties.
// Resets to 0 on reload, but rehydrated keys carry an earlier Date.now(), so a
// fresh add (later timestamp) can never collide with them.
let lineKeySeq = 0;

// Cart survives reloads/direct links via localStorage. Only the lines are
// persisted - the estimate is server-derived and re-fetched, so a stale price
// is never rehydrated.
export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      neededBy: '',
      estimate: null,
      estimating: false,
      estimateError: null,
      unavailableProductIds: [],

      addLine: (product, variant, customization, qty = 1) => {
        // Floor at the product's minimum order quantity (default 1) so a line
        // never enters the cart below MOQ - the server rejects it at quote time.
        const moq = product.min_order_qty ?? 1;
        const safeQty = Number.isFinite(qty) ? Math.max(moq, Math.floor(qty)) : moq;
        const sig = lineSig(product, variant, customization);
        set((s) => {
          // Merge into an existing identical line (same product + variant +
          // customization) so re-adding a plain item bumps its qty rather than
          // stacking a duplicate row.
          const idx = s.lines.findIndex((l) => lineSig(l.product, l.variant, l.customization) === sig);
          if (idx !== -1) {
            const lines = s.lines.slice();
            lines[idx] = { ...lines[idx], qty: lines[idx].qty + safeQty };
            return { lines };
          }
          const key = `${product.id}:${variant?.id ?? 0}:${Date.now()}-${(lineKeySeq += 1)}`;
          return { lines: [...s.lines, { key, product, variant, qty: safeQty, customization }] };
        });
      },

      updateQty: (key, qty) =>
        set((s) => ({
          // Guard against NaN (an emptied number input sends Number('') === NaN)
          // and floor at the line's product MOQ (default 1), so the cart can't
          // hold a sub-minimum qty that only fails later at quote submission.
          lines: s.lines.map((l) => {
            if (l.key !== key) return l;
            const moq = l.product.min_order_qty ?? 1;
            return { ...l, qty: Number.isFinite(qty) ? Math.max(moq, Math.floor(qty)) : moq };
          }),
        })),

      removeLine: (key) => set((s) => ({ lines: s.lines.filter((l) => l.key !== key) })),

      // In-place replace of a single line, addressed by key (used by the cart
      // "Edit design" flow). No dedup/merge: an edit that happens to match
      // another line stays its own row - merging would silently delete it.
      updateLine: (key, patch) =>
        set((s) => ({
          lines: s.lines.map((l) => {
            if (l.key !== key) return l;
            const moq = l.product.min_order_qty ?? 1;
            return {
              ...l,
              customization: patch.customization,
              variant: patch.variant !== undefined ? patch.variant : l.variant,
              qty:
                patch.qty !== undefined
                  ? Math.max(moq, Math.floor(Number.isFinite(patch.qty) ? patch.qty : moq))
                  : l.qty,
            };
          }),
        })),

      setNeededBy: (date) => set({ neededBy: date }),

      clear: () => set({ lines: [], neededBy: '', estimate: null, estimateError: null }),

      refreshEstimate: async () => {
        const { lines } = get();
        if (lines.length === 0) {
          set({ estimate: null, estimateError: null, unavailableProductIds: [] });
          return;
        }
        set({ estimating: true, estimateError: null });
        try {
          const { data } = await api.post<PriceEstimate>('/price-estimate', {
            line_items: lines.map((l) => ({
              product_id: l.product.id,
              variant_id: l.variant?.id ?? null,
              qty: l.qty,
              has_customization: hasCustomization(l.customization),
              logo_size: l.customization.logo_size ?? null,
              // M9: the text-personalization fee is charged on the final quote, so
              // it must be in the estimate too - otherwise the buyer is shown a
              // lower price than they're billed.
              has_text: Boolean(l.customization.text),
            })),
          });
          set({ estimate: data, estimating: false, unavailableProductIds: [] });
        } catch (err) {
          // M10: pull the per-line unavailable list off the 422 so the cart can
          // flag exactly which item to remove.
          const data = (err as { response?: { data?: { unavailable?: { product_id: number }[] } } })?.response?.data;
          const unavailableProductIds = Array.isArray(data?.unavailable)
            ? data!.unavailable.map((u) => Number(u.product_id))
            : [];
          set({ estimating: false, estimateError: apiError(err), unavailableProductIds });
        }
      },
    }),
    {
      name: 'giftlab-cart',
      partialize: (s) => ({ lines: s.lines, neededBy: s.neededBy }),
    },
  ),
);
